import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { NavigateFunction } from 'react-router-dom';

import { getUrlParamsFetch, notFoundIdHandler } from '@/shared';
import { erpApi } from '@/shared/axios/erp-api';
import { RecaudacionesActionsKeysEnum, urlBackend } from '@/shared/constants';
import { Saldo, SaldoPagingRes } from '@/shared/interfaces';
import { useUiStore } from '@/store/ui';

const { get, post, put, remove } = erpApi();

///* tanSctack query
export const useFetchSaldos = (params?: GetSaldosParams) => {
  return useQuery({
    queryKey: [
      RecaudacionesActionsKeysEnum.SALDOS,
      ...Object.values(params || {}),
    ],
    queryFn: async () => {
      return getSaldos(params);
    },
  });
};

export const useGetSaldo = (id: string) => {
  return useQuery({
    queryKey: ['saldo', id],
    queryFn: async () => {
      return getSaldo(id);
    },
  });
};

export const useCreateSaldo = (
  navigate: NavigateFunction,
  returnUrl: string,
  redirect = true
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createSaldo,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [RecaudacionesActionsKeysEnum.SALDOS],
      });
    },
    onSettled: () => {
      redirect && navigate(returnUrl);
    },
  });
};

export const useUpdateSaldo = (
  navigate: NavigateFunction,
  returnUrl: string,
  redirect = true
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateSaldo,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [RecaudacionesActionsKeysEnum.SALDOS],
      });
    },
    onSettled: () => {
      redirect && navigate(returnUrl);
    },
  });
};

export const useDeleteSaldo = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteSaldo,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [RecaudacionesActionsKeysEnum.SALDOS],
      });
    },
  });
};

///* axios
export type GetSaldosParams = {
  page?: number;
  page_size?: number;

  // filters
  estado?: boolean;
  descripcion?: string;
  fecha?: string;
  rubro_origen?: number; // fk
  rubro_destino?: number; // fk
  cliente?: string; // fk
};

export type CreateSaldoParams = Omit<Saldo, 'id_saldo'>;
export type UpdateSaldoParams = {
  id: string;
  data: CreateSaldoParams;
};

export const getSaldos = async (params?: GetSaldosParams) => {
  const queryParams = getUrlParamsFetch(params || {});

  return get<SaldoPagingRes>(
    `${urlBackend}api/v1/saldo/?${queryParams}`,
    true
  );
};

export const getSaldo = async (id: string) => {
  const res = await get<Saldo>(`${urlBackend}api/v1/saldo/${id}/`, true);

  notFoundIdHandler(res);
  return res;
};

export const createSaldo = async (data: CreateSaldoParams) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await post<Saldo>(`${urlBackend}api/v1/saldo/`, data, true);

  setAlert(true, res.message, res.status);
  return res;
};

export const updateSaldo = async ({ id, data }: UpdateSaldoParams) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await put<Saldo>(`${urlBackend}api/v1/saldo/${id}/`, data, true);

  setAlert(true, res.message, res.status);
  return res;
};

export const deleteSaldo = async (id: string) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await remove(`${urlBackend}api/v1/saldo/${id}/`, true);

  setAlert(true, res.message, res.status);
  return res;
};
